import React, { useEffect, useState } from "react";
import { Settings, Calendar, Users, Mail } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

export default function PayrollSettingsTab() {
  const [settings, setSettings] = useState({
    cutoff_day:        25,
    processing_day:    28,
    disbursement_day:  1,
    approval_levels:   1,
    approver_1_role:   "payroll_manager",
    approver_2_role:   "",
    payslip_auto_email: true,
    payslip_portal:    true,
    payslip_password:  "dob",
    currency:          "INR",
    fiscal_year_start: "April",
    lop_deduct_method: "calendar_days",
  });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API}/api/admin/payroll/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d && typeof d === "object") setSettings(s => ({ ...s, ...d })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/api/admin/payroll/settings`, {
        method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg({ type: "success", text: "Settings saved." });
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className={`${card} p-5`}><div className="h-48 rounded-lg bg-white/5 animate-pulse" /></div>;

  return (
    <div className="space-y-5">

      {/* Payroll Calendar */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Calendar size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Payroll Calendar</span>
        </div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Cut-off Day</label>
            <input type="number" min="1" max="31" className={inputCls} value={settings.cutoff_day}
              onChange={e => set("cutoff_day", Number(e.target.value))} />
            <p className="text-xs text-white/20 mt-1">Attendance/leave data locked on this day</p>
          </div>
          <div>
            <label className={labelCls}>Processing Day</label>
            <input type="number" min="1" max="31" className={inputCls} value={settings.processing_day}
              onChange={e => set("processing_day", Number(e.target.value))} />
            <p className="text-xs text-white/20 mt-1">Payroll calculated on this day</p>
          </div>
          <div>
            <label className={labelCls}>Disbursement Day</label>
            <input type="number" min="1" max="31" className={inputCls} value={settings.disbursement_day}
              onChange={e => set("disbursement_day", Number(e.target.value))} />
            <p className="text-xs text-white/20 mt-1">Bank transfer initiated on this day</p>
          </div>
          <div>
            <label className={labelCls}>Fiscal Year Start</label>
            <select className={inputCls} value={settings.fiscal_year_start}
              onChange={e => set("fiscal_year_start", e.target.value)}>
              {["January","April"].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Approval Workflow */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Users size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Approval Workflow</span>
        </div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Approval Levels</label>
            <select className={inputCls} value={settings.approval_levels}
              onChange={e => set("approval_levels", Number(e.target.value))}>
              <option value={1}>1 — Single approval</option>
              <option value={2}>2 — Dual approval</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Approver Level 1 Role</label>
            <input className={inputCls} value={settings.approver_1_role}
              onChange={e => set("approver_1_role", e.target.value)} placeholder="e.g. payroll_manager" />
          </div>
          {settings.approval_levels >= 2 && (
            <div>
              <label className={labelCls}>Approver Level 2 Role</label>
              <input className={inputCls} value={settings.approver_2_role}
                onChange={e => set("approver_2_role", e.target.value)} placeholder="e.g. cfo" />
            </div>
          )}
        </div>
      </div>

      {/* Payslip Distribution */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Mail size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Payslip Distribution</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!settings.payslip_auto_email}
                onChange={e => set("payslip_auto_email", e.target.checked)} className="w-4 h-4 rounded accent-[#00ff90]" />
              <div>
                <p className="text-sm text-white/60">Auto-email payslips</p>
                <p className="text-xs text-white/25">Send to employee's work email on disbursement</p>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!settings.payslip_portal}
                onChange={e => set("payslip_portal", e.target.checked)} className="w-4 h-4 rounded accent-[#00ff90]" />
              <div>
                <p className="text-sm text-white/60">Employee portal access</p>
                <p className="text-xs text-white/25">Employees can download from the portal</p>
              </div>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>PDF Password</label>
              <select className={inputCls} value={settings.payslip_password}
                onChange={e => set("payslip_password", e.target.value)}>
                <option value="dob">Date of birth (DDMMYYYY)</option>
                <option value="pan">PAN number</option>
                <option value="emp_code">Employee code</option>
                <option value="none">No password</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>LOP Deduction Method</label>
              <select className={inputCls} value={settings.lop_deduct_method}
                onChange={e => set("lop_deduct_method", e.target.value)}>
                <option value="calendar_days">Calendar days (actual month days)</option>
                <option value="working_days">Working days only</option>
                <option value="fixed_30">Fixed 30 days</option>
                <option value="fixed_26">Fixed 26 days</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {msg && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${
          msg.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"
        }`}>{msg.text}</div>
      )}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
          className="text-xs px-6 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
}